export enum DocumentTemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export enum DocumentTemplateType {
  QUOTE_DOCUMENT = 'QUOTE_DOCUMENT',
  POLICY_DOCUMENT = 'POLICY_DOCUMENT',
  CLAIM_DOCUMENT = 'CLAIM_DOCUMENT',
  REPORT_DOCUMENT = 'REPORT_DOCUMENT',
  CERTIFICATE = 'CERTIFICATE',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  OTHER = 'OTHER'
}

export enum DocumentProcessingStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export interface DocumentTemplate {
  id: string;
  name: string;
  description?: string;
  type: DocumentTemplateType;
  status: DocumentTemplateStatus;
  organizationId?: string;
  createdById: string;
  tags: string[];
  currentVersionId?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  
  // Relations
  organization?: Organization;
  createdBy?: User;
  versions?: DocumentTemplateVersion[];
  currentVersion?: DocumentTemplateVersion;
}

export interface DocumentTemplateVersion {
  id: string;
  templateId: string;
  version: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  fileUrl: string;
  uploadedById: string;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  
  // Relations
  template?: DocumentTemplate;
  uploadedBy?: User;
}

export interface GeneratedDocument {
  id: string;
  templateId?: string; // Now stored in metadata
  templateVersionId?: string; // Now stored in metadata
  quoteId?: string;
  policyId?: string;
  claimId?: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  fileUrl: string;
  generatedById: string; // Maps to createdById in backend
  metadata: Record<string, unknown>;
  createdAt: string;
  
  // Relations
  template?: DocumentTemplate; // Retrieved from metadata.templateId
  templateVersion?: DocumentTemplateVersion; // Retrieved from metadata.templateVersionId
  createdBy?: User; // Renamed from generatedBy
  updatedBy?: User; // New field
}

export interface DocumentProcessingJob {
  id: string;
  templateId?: string;
  templateVersionId?: string;
  status: DocumentProcessingStatus;
  inputData: Record<string, unknown>;
  outputFileUrl?: string;
  errorMessage?: string;
  processingStartedAt?: string;
  processingCompletedAt?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  
  // Relations
  template?: DocumentTemplate;
  templateVersion?: DocumentTemplateVersion;
}

// Frontend-specific types
export interface DocumentTemplateFilters {
  type?: DocumentTemplateType;
  status?: DocumentTemplateStatus;
  tags?: string[];
  search?: string;
  organizationId?: string;
  page?: number;
  limit?: number;
}

export interface DocumentTemplateStats {
  total: number;
  byStatus: Record<DocumentTemplateStatus, number>;
  byType: Record<DocumentTemplateType, number>;
  recentUploads: number;
  totalVersions: number;
}

export interface DocumentTemplateUploadData {
  name: string;
  description?: string;
  type: DocumentTemplateType;
  tags: string[];
  file: File;
}

// Helper functions
export const getStatusLabel = (status: DocumentTemplateStatus): string => {
  switch (status) {
    case DocumentTemplateStatus.DRAFT:
      return 'Draft';
    case DocumentTemplateStatus.ACTIVE:
      return 'Active';
    case DocumentTemplateStatus.ARCHIVED:
      return 'Archived';
    default:
      return status;
  }
};

export const getTypeLabel = (type: DocumentTemplateType): string => {
  switch (type) {
    case DocumentTemplateType.QUOTE_DOCUMENT:
      return 'Quote Document';
    case DocumentTemplateType.POLICY_DOCUMENT:
      return 'Policy Document';
    case DocumentTemplateType.CLAIM_DOCUMENT:
      return 'Claim Document';
    case DocumentTemplateType.REPORT_DOCUMENT:
      return 'Report Document';
    case DocumentTemplateType.CERTIFICATE:
      return 'Certificate';
    case DocumentTemplateType.EMAIL:
      return 'Email Template';
    case DocumentTemplateType.SMS:
      return 'SMS Template';
    case DocumentTemplateType.OTHER:
      return 'Other';
    default:
      return type;
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Base types that might be referenced
interface Organization {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  name?: string;
} 